import {
  type BankAccountVerifyRequestAccountTypeEnum,
  BankAccountVerifyResultOutcomeEnum,
} from "@credit-acceptance/step6-bff-client";
import { toast } from "@credit-acceptance/uikit";
import { useQueryClient } from "@tanstack/react-query";
import { Info, X } from "lucide-react";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getErrorMessage, getProblemDetail } from "../../lib/query/errors";
import { cn } from "../../lib/utils";
import { stipulationKeys } from "./api/stipulationKeys";
import { useVerifyBankAccount } from "./api/useVerifyBankAccount";
import type { BankAccountOwnerOption } from "./bankAccountOwnerOptions";
import BankVerificationHelpModal from "./BankVerificationHelpModal";

interface BankVerificationModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId?: string;
  ownerOptions: BankAccountOwnerOption[];
  readOnly?: boolean;
}

interface FieldErrors {
  ownerType?: string;
  routingNumber?: string;
  accountNumber?: string;
}

const ROUTING_NUMBER_PATTERN = /^\d{9}$/;

const BANNER_STYLES_BY_OUTCOME: Record<BankAccountVerifyResultOutcomeEnum, string> = {
  [BankAccountVerifyResultOutcomeEnum.Verified]: "bg-success text-success-foreground",
  [BankAccountVerifyResultOutcomeEnum.NotVerified]: "bg-destructive/10 text-destructive",
  [BankAccountVerifyResultOutcomeEnum.Pending]: "bg-warning text-warning-foreground",
  [BankAccountVerifyResultOutcomeEnum.Unknown]: "bg-warning text-warning-foreground",
};

// Modal opened from the stip row's Verify button. Owner options are pre-filtered
// by the row to this applicant (and account type, when the row's stipCode is
// recognized); submitting calls the manual verify-bank-account endpoint and
// shows the outcome as a banner.
function BankVerificationModal({
  open,
  onOpenChange,
  approvalId,
  ownerOptions,
  readOnly = false,
}: BankVerificationModalProperties) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [ownerType, setOwnerType] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [helpOpen, setHelpOpen] = useState(false);
  const mutation = useVerifyBankAccount();

  const verified = mutation.data?.result?.outcome === BankAccountVerifyResultOutcomeEnum.Verified;
  const formDisabled = readOnly || mutation.isPending || verified;
  const hasEmptyField = !ownerType || !routingNumber.trim() || !accountNumber.trim();

  function resetForm() {
    setOwnerType("");
    setRoutingNumber("");
    setAccountNumber("");
    setFieldErrors({});
    mutation.reset();
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  }

  function validate(selectedOption: BankAccountOwnerOption | undefined): FieldErrors {
    const errors: FieldErrors = {};
    if (!selectedOption) {
      errors.ownerType = t("stipulations.bankVerification.validationOwnerRequired");
    }
    if (!ROUTING_NUMBER_PATTERN.test(routingNumber)) {
      errors.routingNumber = t("stipulations.bankVerification.validationRoutingNumber");
    }
    if (!accountNumber.trim()) {
      errors.accountNumber = t("stipulations.bankVerification.validationAccountNumber");
    }
    return errors;
  }

  // Clears a stale outcome/error banner as soon as the user edits the form or
  // resubmits, so a previous attempt's result is never shown alongside new
  // input or a new in-flight request.
  function clearBanner() {
    if (mutation.data || mutation.error) {
      mutation.reset();
    }
  }

  function handleOwnerTypeChange(value: string) {
    clearBanner();
    setOwnerType(value);
  }

  function handleRoutingNumberChange(event: ChangeEvent<HTMLInputElement>) {
    clearBanner();
    setRoutingNumber(event.target.value);
  }

  function handleAccountNumberChange(event: ChangeEvent<HTMLInputElement>) {
    clearBanner();
    setAccountNumber(event.target.value);
  }

  function handleSubmit() {
    if (readOnly) {
      toast.error(t("workbench.actionNotAllowed"));
      return;
    }
    clearBanner();
    const selectedOption = ownerOptions.find((option) => option.value === ownerType);
    const errors = validate(selectedOption);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0 || !selectedOption || !approvalId) {
      return;
    }

    mutation.mutate({
      approvalId,
      applicantPk: selectedOption.applicantPk,
      stipCode: selectedOption.stipCode,
      routingNumber,
      accountNumber,
      accountType: selectedOption.accountType as BankAccountVerifyRequestAccountTypeEnum,
    });
  }

  function handleDone() {
    if (approvalId) {
      void queryClient.invalidateQueries({ queryKey: stipulationKeys.listsForApproval(approvalId) });
    }
    handleOpenChange(false);
  }

  // step6-bff classifies some verification failures (e.g. max attempts
  // reached) into a curated ProblemDetail with caps2-authored messageTitle/
  // messageDetail. Prefer that reason-specific copy over the generic
  // "Couldn't verify bank account: ..." fallback whenever it's present.
  const problemDetail = mutation.isError ? getProblemDetail(mutation.error) : undefined;
  const curatedTitle =
    typeof problemDetail?.details?.messageTitle === "string" ? problemDetail.details.messageTitle : undefined;
  const curatedDetail =
    typeof problemDetail?.details?.messageDetail === "string" ? problemDetail.details.messageDetail : undefined;

  const outcome = mutation.data?.result?.outcome;
  const banner = mutation.isError
    ? {
        className: "bg-destructive/10 text-destructive",
        title:
          curatedTitle ?? t("stipulations.bankVerification.submitError", { message: getErrorMessage(mutation.error) }),
        detail: curatedDetail,
      }
    : outcome
      ? {
          className: BANNER_STYLES_BY_OUTCOME[outcome],
          title: mutation.data?.result?.messageTitle ?? t(`stipulations.bankVerification.outcome.${outcome}`),
          detail: mutation.data?.result?.messageDetail,
        }
      : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent showCloseButton={false} className="max-w-2xl gap-0 overflow-hidden bg-white p-0">
          <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
            <DialogTitle className="text-primary-foreground text-sm font-semibold">
              {t("stipulations.bankVerification.title")}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("stipulations.bankVerification.description")}</DialogDescription>
            <DialogClose className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors">
              <X className="size-4.5" />
              <span className="sr-only">{t("stipulations.bankVerification.close")}</span>
            </DialogClose>
          </div>

          <div className="flex flex-col gap-3 p-6">
            <div>
              <h3 className="text-foreground text-xs font-semibold">{t("stipulations.bankVerification.heading")}</h3>
              <p className="text-muted-foreground text-xs">{t("stipulations.bankVerification.subheading")}</p>
            </div>

            {banner && (
              <div className={cn("rounded-md px-3 py-2 text-xs", banner.className)}>
                <p className="font-semibold">{banner.title}</p>
                {banner.detail && <p>{banner.detail}</p>}
              </div>
            )}

            <div className="flex items-end gap-3">
              <div className="flex flex-1 flex-col gap-1">
                <label htmlFor="bank-verification-owner-type" className="text-muted-foreground text-xs">
                  {t("stipulations.bankVerification.ownerTypePlaceholder")}
                </label>
                <Select value={ownerType} onValueChange={handleOwnerTypeChange} disabled={formDisabled}>
                  <SelectTrigger
                    id="bank-verification-owner-type"
                    aria-invalid={Boolean(fieldErrors.ownerType)}
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ownerOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors.ownerType && <span className="text-destructive text-xs">{fieldErrors.ownerType}</span>}
              </div>

              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-1 text-xs">
                  <label htmlFor="bank-verification-routing-number" className="text-muted-foreground">
                    {t("stipulations.bankVerification.routingNumberPlaceholder")}
                  </label>
                  <button
                    type="button"
                    aria-label={t("stipulations.bankVerification.routingNumberHint")}
                    className="text-muted-foreground"
                    onClick={() => setHelpOpen(true)}
                  >
                    <Info aria-hidden="true" className="size-3.5" />
                  </button>
                </div>
                <Input
                  id="bank-verification-routing-number"
                  value={routingNumber}
                  onChange={handleRoutingNumberChange}
                  disabled={formDisabled}
                  aria-invalid={Boolean(fieldErrors.routingNumber)}
                />
                {fieldErrors.routingNumber && (
                  <span className="text-destructive text-xs">{fieldErrors.routingNumber}</span>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-1 text-xs">
                  <label htmlFor="bank-verification-account-number" className="text-muted-foreground">
                    {t("stipulations.bankVerification.accountNumberPlaceholder")}
                  </label>
                  <button
                    type="button"
                    aria-label={t("stipulations.bankVerification.accountNumberHint")}
                    className="text-muted-foreground"
                    onClick={() => setHelpOpen(true)}
                  >
                    <Info aria-hidden="true" className="size-3.5" />
                  </button>
                </div>
                <Input
                  id="bank-verification-account-number"
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  disabled={formDisabled}
                  aria-invalid={Boolean(fieldErrors.accountNumber)}
                />
                {fieldErrors.accountNumber && (
                  <span className="text-destructive text-xs">{fieldErrors.accountNumber}</span>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 pb-6">
            {verified ? (
              <Button type="button" onClick={handleDone}>
                {t("stipulations.bankVerification.done")}
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={formDisabled || hasEmptyField}>
                {mutation.isPending
                  ? t("stipulations.bankVerification.submitting")
                  : t("stipulations.bankVerification.submit")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BankVerificationHelpModal open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}

export default BankVerificationModal;
